import React, { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { DEFAULT_BUSINESS_PROFILE_QUESTIONS } from "./businessProfileQuestions";
import CalendarDatePicker from "./CalendarDatePicker";
import "./BusinessProfileStep.css";

const isQuestionRequired = (question) =>
  question.required === true ||
  question.isRequired === true ||
  question.mandatory === true;

const BusinessProfileStep = ({
  initialData = {},
  questions = DEFAULT_BUSINESS_PROFILE_QUESTIONS,
  onChange,
  onFinish,
  onBackFromFirstPage,
  footerAction,
  guideTarget,
  showAllSections = false,
  questionsPerPage,
  slidePages = false,
  nextLabel = "Continue",
  finishLabel = "Finish and continue",
  showBackArrow = false,
  showNextArrow = false,
  renderNavigation,
}) => {
  const [answers, setAnswers] = useState(initialData);
  const [requiredErrors, setRequiredErrors] = useState({});
  const [pageIndex, setPageIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const shouldReduceMotion = useReducedMotion();
  const configuredSections = questions.filter(
    (section) => section.questions.length > 0,
  );
  const configuredPages = questionsPerPage
    ? configuredSections
        .flatMap((section) =>
          section.questions.map((question) => ({
            sectionTitle: section.title,
            sectionDescription: section.description,
            question,
          })),
        )
        .reduce((pages, entry, index) => {
          const pageIndex = Math.floor(index / questionsPerPage);
          if (!pages[pageIndex]) {
            pages[pageIndex] = {
              title: entry.sectionTitle,
              description: entry.sectionDescription,
              questions: [],
              sectionTitles: new Set(),
            };
          }
          const page = pages[pageIndex];
          page.questions.push(entry.question);
          page.sectionTitles.add(entry.sectionTitle);
          if (page.sectionTitles.size > 1) {
            page.title = "Website project questions";
            page.description = "";
          }
          return pages;
        }, [])
    : configuredSections;
  const page = configuredPages[pageIndex] || configuredPages[0];
  const isLastPage = pageIndex === configuredPages.length - 1;
  const handleBack = () =>
    pageIndex === 0
      ? onBackFromFirstPage?.()
      : navigatePage(pageIndex - 1);
  const handleNext = () => {
    const missingAnswers = page.questions
      .filter((question) => {
        if (!isQuestionRequired(question)) return false;
        const answer = answers[question.id];
        return Array.isArray(answer)
          ? answer.length === 0
          : !String(answer || "").trim();
      })
      .reduce((errors, question) => {
        errors[question.id] = `${question.label} is required.`;
        return errors;
      }, {});

    setRequiredErrors(missingAnswers);
    if (Object.keys(missingAnswers).length > 0) return;

    if (isLastPage) onFinish?.();
    else navigatePage(pageIndex + 1);
  };
  const slideVariants = {
    enter: (slideDirection) => ({
      x: slideDirection > 0 ? 40 : -40,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: shouldReduceMotion
        ? { duration: 0.18 }
        : {
            type: "spring",
            stiffness: 400,
            damping: 30,
            duration: 0.3,
          },
    },
    exit: (slideDirection) => ({
      x: shouldReduceMotion ? 0 : slideDirection < 0 ? 40 : -40,
      opacity: 0,
      transition: {
        duration: shouldReduceMotion ? 0.12 : 0.2,
      },
    }),
  };

  const navigatePage = (nextIndex) => {
    setDirection(nextIndex > pageIndex ? 1 : -1);
    setPageIndex(nextIndex);
  };

  const updateAnswer = (questionId, value) => {
    const nextAnswers = { ...answers, [questionId]: value };
    setAnswers(nextAnswers);
    onChange(nextAnswers);
    setRequiredErrors((current) => {
      if (!current[questionId]) return current;
      const nextErrors = { ...current };
      delete nextErrors[questionId];
      return nextErrors;
    });
  };

  const toggleOption = (questionId, option) => {
    const selected = answers[questionId] || [];
    updateAnswer(
      questionId,
      selected.includes(option)
        ? selected.filter((item) => item !== option)
        : [...selected, option],
    );
  };

  const renderQuestion = (question, index) => {
    const inputId = `business-profile-${question.id}`;
    const value = answers[question.id] || "";

    return (
      <motion.div
        className="profile-question"
        key={question.id}
        initial={shouldReduceMotion ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: shouldReduceMotion ? 0 : 0.32,
          delay: shouldReduceMotion ? 0 : index * 0.08,
        }}
      >
        <label className="profile-question-label" htmlFor={inputId}>
          {question.label}
          {isQuestionRequired(question) && (
            <>
              {" "}
              <span className="required" aria-hidden="true">
                *
              </span>
            </>
          )}
        </label>

        {question.type === "textarea" ? (
          <textarea
            id={inputId}
            value={value}
            onChange={(event) => updateAnswer(question.id, event.target.value)}
            placeholder={question.placeholder || "Share as much as you know so far"}
            rows={3}
          />
        ) : question.type === "select" ? (
          <select
            id={inputId}
            value={value}
            onChange={(event) => updateAnswer(question.id, event.target.value)}
          >
            <option value="">Choose an answer</option>
            {question.options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        ) : question.type === "multi-select" ? (
          <>
            <div
              className="profile-option-list"
              role="group"
              aria-label={question.label}
            >
              {question.options.map((option) => {
                const selected = (answers[question.id] || []).includes(option);

                return (
                  <label
                    className={`profile-option ${selected ? "selected" : ""}`}
                    key={option}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleOption(question.id, option)}
                    />
                    <span>{option}</span>
                  </label>
                );
              })}
            </div>
            {(answers[question.id] || []).includes("Other") && (
              <div className="profile-conditional-question">
                <label
                  className="profile-question-label"
                  htmlFor="business-profile-other-support"
                >
                  What other support would be useful?
                </label>
                <input
                  id="business-profile-other-support"
                  type="text"
                  value={answers.otherSupportDetails || ""}
                  onChange={(event) =>
                    updateAnswer("otherSupportDetails", event.target.value)
                  }
                />
              </div>
            )}
          </>
        ) : question.type === "date" ? (
          <CalendarDatePicker
            id={inputId}
            value={value}
            onChange={(date) => updateAnswer(question.id, date)}
          />
        ) : (
          <input
            id={inputId}
            type="text"
            value={value}
            placeholder={question.placeholder || ""}
            onChange={(event) => updateAnswer(question.id, event.target.value)}
          />
        )}
        {requiredErrors[question.id] && (
          <span className="field-error" role="alert">
            {requiredErrors[question.id]}
          </span>
        )}
      </motion.div>
    );
  };

  if (!page) {
    return null;
  }

  return (
    <section
      className="business-profile-step"
      aria-labelledby={`profile-page-title-${showAllSections ? 0 : pageIndex}`}
      data-tour={guideTarget}
    >
      {showAllSections ? (
        configuredPages.map((section, sectionIndex) => (
          <motion.div
            key={`${section.title}-${sectionIndex}`}
            className="profile-question-page"
            initial={shouldReduceMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: shouldReduceMotion ? 0 : 0.24 }}
          >
            <div className="profile-page-heading">
              <h3 id={`profile-page-title-${sectionIndex}`}>{section.title}</h3>
              {section.description && <p>{section.description}</p>}
            </div>
            <div className="profile-question-list">
              {section.questions.map(renderQuestion)}
            </div>
          </motion.div>
        ))
      ) : (
        <>
          <div className="profile-progress">
            <div
              className="profile-progress-fill"
              style={{
                width: `${((pageIndex + 1) / configuredPages.length) * 100}%`,
              }}
            />
          </div>
          <div className="profile-page-meta">
            <span>{page.title}</span>
            <span>
              Section {pageIndex + 1} of {configuredPages.length}
            </span>
          </div>
          <AnimatePresence
            mode="wait"
            custom={direction}
            initial={false}
          >
            <motion.div
              key={pageIndex}
              className="profile-question-page"
              custom={direction}
              variants={
                slidePages
                  ? slideVariants
                  : {
                      enter: { opacity: 0 },
                      center: { opacity: 1 },
                      exit: { opacity: 0 },
                    }
              }
              initial="enter"
              animate="center"
              exit="exit"
            >
              <div className="profile-page-heading">
                <h3 id={`profile-page-title-${pageIndex}`}>{page.title}</h3>
                <p>{page.description}</p>
              </div>
              <div className="profile-question-list">
                {page.questions.map(renderQuestion)}
              </div>
            </motion.div>
          </AnimatePresence>
          {renderNavigation
            ? renderNavigation({
                pageIndex,
                isLastPage,
                canGoBack: pageIndex > 0 || Boolean(onBackFromFirstPage),
                onBack: handleBack,
                onNext: handleNext,
              })
            : (
                <div className="profile-page-navigation">
                  <button
                    type="button"
                    className="profile-page-back"
                    onClick={handleBack}
                    disabled={pageIndex === 0 && !onBackFromFirstPage}
                    aria-label="Back"
                  >
                    {showBackArrow ? (
                      <>
                        <i className="fas fa-arrow-left" aria-hidden="true" />
                        <span>Back</span>
                      </>
                    ) : (
                      "Back"
                    )}
                  </button>
                  <span className="profile-page-hint">
                    Your answers help us tailor your onboarding support.
                  </span>
                  <div className="profile-page-actions">
                    {footerAction}
                    <button
                      type="button"
                      className="profile-page-next"
                      onClick={handleNext}
                    >
                      {isLastPage ? finishLabel : nextLabel}
                      {showNextArrow && !isLastPage && (
                        <i className="fas fa-arrow-right" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                </div>
              )}
        </>
      )}
    </section>
  );
};

export default BusinessProfileStep;
